/**
 * Client-side persistence (idb-keyval) for prefs + identity token, with a
 * graceful in-memory fallback when IndexedDB is unavailable (private browsing).
 */
import { get, set } from 'idb-keyval';

const PREFS_KEY = 'jgolf_prefs_v4';
const LEGACY_PREFS_KEY = 'jgolf_prefs_v3';

export interface Prefs {
  version: 4;
  nickname: string;
  secretToken: string;
  sound: boolean;
  volume: number;
  music: boolean;
  musicVolume: number;
  reducedMotion: boolean;
}

function randomToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function randomNickname(): string {
  return `Player${Math.floor(1000 + Math.random() * 9000)}`;
}

let cache: Prefs | null = null;

export async function loadPrefs(): Promise<Prefs> {
  if (cache) return cache;
  let stored: Partial<Prefs> | undefined;
  try {
    // Migrate from v3 (preserving nickname/identity) when v4 isn't present yet.
    stored = (await get<Prefs>(PREFS_KEY)) ?? (await get<Prefs>(LEGACY_PREFS_KEY));
  } catch {
    /* IndexedDB unavailable */
  }
  cache = {
    version: 4,
    nickname: stored?.nickname || randomNickname(),
    secretToken: stored?.secretToken || randomToken(),
    sound: stored?.sound ?? true,
    volume: stored?.volume ?? 0.6,
    music: stored?.music ?? true,
    musicVolume: stored?.musicVolume ?? 0.4,
    reducedMotion: stored?.reducedMotion ?? false,
  };
  await persist();
  userId = await deriveUserId(cache.secretToken);
  return cache;
}

async function persist(): Promise<void> {
  if (!cache) return;
  try {
    await set(PREFS_KEY, cache);
  } catch {
    /* ignore */
  }
}

export function getPrefs(): Prefs {
  if (!cache) throw new Error('prefs not loaded');
  return cache;
}

export async function updatePrefs(patch: Partial<Prefs>): Promise<Prefs> {
  cache = { ...(cache as Prefs), ...patch };
  await persist();
  return cache;
}

let userId: string | null = null;

/**
 * Stable player id derived from the secret token (SHA-256), so stats and
 * leaderboards persist across sessions while the token itself never leaves
 * the browser un-hashed. Computed once during `loadPrefs`.
 *
 * Append `?guest` to the URL for a throwaway per-tab identity (handy for
 * testing multiplayer in two tabs of the same browser).
 */
export function getUserId(): string {
  if (isGuestTab()) return guestId();
  if (userId) return userId;
  // loadPrefs always runs before any networking; this is a belt-and-braces
  // fallback for unexpected call orders.
  return guestId();
}

function isGuestTab(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('guest');
  } catch {
    return false;
  }
}

function guestId(): string {
  const KEY = 'jgolf_user_id';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

async function deriveUserId(token: string): Promise<string> {
  try {
    if (crypto?.subtle) {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
      const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
      return `u-${hex.slice(0, 24)}`;
    }
  } catch {
    /* SubtleCrypto unavailable (plain-http dev) — fall through */
  }
  let h1 = 2166136261 >>> 0;
  let h2 = 0x9e3779b9 >>> 0;
  for (let i = 0; i < token.length; i++) {
    h1 = Math.imul(h1 ^ token.charCodeAt(i), 16777619) >>> 0;
    h2 = Math.imul(h2 ^ token.charCodeAt(i), 2246822519) >>> 0;
  }
  return `u-${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}
